import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2, Users, TrendingUp, Trash2, ChevronDown, Bell, Contact as ContactIcon, X, Search, Pencil, Filter } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CsvControlsV2 } from "@/app/components/CsvImportWizard";
import LifecycleDraftModal from "@/app/components/LifecycleDraftModal";

const STAGES = [
  { key: "new", label: "New" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "negotiation", label: "Negotiation" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];
const CURRENCIES = ["USD", "EUR", "INR", "AED"];

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

function fmtMoney(cur, val) {
  const n = Number(val || 0);
  const opts = { maximumFractionDigits: n === Math.floor(n) ? 0 : 2 };
  return `${cur} ${n.toLocaleString(undefined, opts)}`;
}

export default function SalesEngine() {
  const [tab, setTab] = useState("pipeline");
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [expandedContact, setExpandedContact] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);

  // Phase 12.7 P0 — Deal→Won lifecycle draft modal
  const [lifecycleDeal, setLifecycleDeal] = useState(null); // {id, title}

  const defaultCurrency = org?.default_currency || "USD";

  const [dealForm, setDealForm] = useState({ title: "", stage: "new", value: "", currency: "" });
  const [companyForm, setCompanyForm] = useState({ name: "", industry: "" });
  const [contactForm, setContactForm] = useState({ name: "", email: "", company_id: "" });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [companyQuery, setCompanyQuery] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [contactNameMode, setContactNameMode] = useState("full"); // "full" | "split"
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");

  // Phase 12.3 — CRM filters
  const [dealSearch, setDealSearch] = useState("");
  const [dealFilters, setDealFilters] = useState({ stage: "", currency: "", owner_id: "", company_id: "", contact_id: "", close_from: "", close_to: "" });
  const [companyFilters, setCompanyFilters] = useState({ status: "", owner_id: "", industry: "", country: "", lead_source: "" });
  const [contactFilters, setContactFilters] = useState({ company_id: "", owner_id: "", country: "", lead_source: "", status: "" });
  const [members, setMembers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ct, d, t, o, pj] = await Promise.all([
        api.get("/companies"),
        api.get("/contacts"),
        api.get("/deals"),
        api.get("/tasks"),
        api.get("/organizations/me"),
        api.get("/projects"),
      ]);
      setCompanies(c.data.companies || []);
      setContacts(ct.data.contacts || []);
      setDeals(d.data.deals || []);
      setTasks(t.data.tasks || []);
      setOrg(o.data.organization || null);
      setProjects(pj.data.projects || []);
      // Best-effort load of members for owner filters (owner may not have access)
      try {
        const m = await api.get("/organizations/me/members");
        setMembers(m.data.members || []);
      } catch { /* member endpoint restricted for non-owners — ignore */ }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load sales.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dealForm.currency && defaultCurrency) setDealForm((f) => ({ ...f, currency: defaultCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrency]);

  const pipelineByCurrency = useMemo(() => {
    const totals = {};
    for (const d of deals) {
      if (["won", "lost"].includes(d.stage)) continue;
      const cur = d.currency || "USD";
      totals[cur] = totals[cur] || { count: 0, value: 0 };
      totals[cur].count += 1;
      totals[cur].value += Number(d.value) || 0;
    }
    return Object.entries(totals).map(([cur, v]) => ({ currency: cur, ...v }));
  }, [deals]);

  const salesMetrics = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const open = deals.filter((d) => !["won", "lost"].includes(d.stage));
    const followUps = open.filter((d) => d.next_follow_up && d.next_follow_up.slice(0, 10) <= today);
    const closing = open.filter((d) => d.close_date && today <= d.close_date.slice(0, 10) && d.close_date.slice(0, 10) <= in30);
    const won = deals.filter((d) => d.stage === "won");
    const lost = deals.filter((d) => d.stage === "lost");
    return {
      openCount: open.length,
      followUps,
      closing,
      won,
      lost,
    };
  }, [deals]);

  const addDeal = async (e) => {
    e.preventDefault();
    if (!dealForm.title.trim()) return;
    try {
      const { data } = await api.post("/deals", {
        title: dealForm.title.trim(),
        stage: dealForm.stage,
        value: Number(dealForm.value) || 0,
        currency: dealForm.currency || defaultCurrency,
      });
      setDeals((d) => [data.deal, ...d]);
      setDealForm({ title: "", stage: "new", value: "", currency: defaultCurrency });
      setDrawerOpen(false);
      toast.success("Deal added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const patchDeal = async (id, changes) => {
    try {
      const prev = deals.find((d) => d.id === id);
      const { data } = await api.patch(`/deals/${id}`, changes);
      setDeals((all) => all.map((d) => (d.id === id ? data.deal : d)));
      // Phase 12.7 P0 — Deal moved to Won → offer AI next-step draft (only on stage transition)
      if (
        changes?.stage === "won" &&
        prev?.stage !== "won" &&
        !data.deal?.won_plan_generated_at
      ) {
        setLifecycleDeal({ id: data.deal.id, title: data.deal.title });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update deal.");
    }
  };

  const removeDeal = async (id) => {
    try {
      await api.delete(`/deals/${id}`);
      setDeals((d) => d.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete deal."); }
  };

  const addCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) return;
    try {
      const { data } = await api.post("/companies", companyForm);
      setCompanies((c) => [data.company, ...c]);
      setCompanyForm({ name: "", industry: "" });
      toast.success("Company added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addContact = async (e) => {
    e.preventDefault();
    const hasFull = contactNameMode === "full" && contactForm.name.trim();
    const hasSplit = contactNameMode === "split" && (contactFirstName.trim() || contactLastName.trim());
    if (!hasFull && !hasSplit && !contactForm.email) return;
    try {
      const payload = { email: contactForm.email };
      if (contactNameMode === "full") {
        payload.display_name = contactForm.name.trim();
      } else {
        payload.first_name = contactFirstName.trim();
        payload.last_name = contactLastName.trim();
      }
      if (contactForm.company_id) payload.company_id = contactForm.company_id;
      const { data } = await api.post("/contacts", payload);
      setContacts((c) => [data.contact, ...c]);
      setContactForm({ name: "", email: "", company_id: "" });
      setContactFirstName(""); setContactLastName("");
      toast.success("Contact added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const patchCompany = async (id, changes) => {
    try {
      const { data } = await api.patch(`/companies/${id}`, changes);
      setCompanies((all) => all.map((c) => (c.id === id ? data.company : c)));
      toast.success("Company updated");
      return data.company;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update company.");
    }
  };

  const removeCompany = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      setCompanies((c) => c.filter((x) => x.id !== id));
      setEditingCompany(null);
      toast.success("Company removed");
    } catch { toast.error("Could not remove company."); }
  };

  const patchContact = async (id, changes) => {
    try {
      const { data } = await api.patch(`/contacts/${id}`, changes);
      setContacts((all) => all.map((c) => (c.id === id ? data.contact : c)));
      toast.success("Contact updated");
      return data.contact;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update contact.");
    }
  };

  const removeContact = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((c) => c.filter((x) => x.id !== id));
      setEditingContact(null);
      toast.success("Contact removed");
    } catch { toast.error("Could not remove contact."); }
  };

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    const { status, owner_id, industry, country, lead_source } = companyFilters;
    return companies.filter((c) => {
      if (q && ![c.name, c.industry, c.website, c.email, c.city, c.state, c.country, c.notes]
          .some((v) => (v || "").toLowerCase().includes(q))) return false;
      if (status && (c.status || "") !== status) return false;
      if (owner_id && c.owner_id !== owner_id) return false;
      if (industry && (c.industry || "").toLowerCase() !== industry.toLowerCase()) return false;
      if (country && (c.country || "").toLowerCase() !== country.toLowerCase()) return false;
      if (lead_source && (c.lead_source || "").toLowerCase() !== lead_source.toLowerCase()) return false;
      return true;
    });
  }, [companies, companyQuery, companyFilters]);

  const filteredContacts = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    const { company_id, owner_id, country, lead_source, status } = contactFilters;
    return contacts.filter((c) => {
      if (q && ![c.display_name, c.first_name, c.last_name, c.name, c.email, c.secondary_email, c.phone, c.mobile, c.job_title, c.role, c.city, c.country]
          .some((v) => (v || "").toLowerCase().includes(q))) return false;
      if (company_id && c.company_id !== company_id) return false;
      if (owner_id && c.owner_id !== owner_id) return false;
      if (country && (c.country || "").toLowerCase() !== country.toLowerCase()) return false;
      if (lead_source && (c.lead_source || "").toLowerCase() !== lead_source.toLowerCase()) return false;
      if (status && (c.status || "") !== status) return false;
      return true;
    });
  }, [contacts, contactQuery, contactFilters]);

  const filteredDeals = useMemo(() => {
    const q = dealSearch.trim().toLowerCase();
    const { stage, currency, owner_id, company_id, contact_id, close_from, close_to } = dealFilters;
    return deals.filter((d) => {
      if (q && ![d.title, d.notes, d.product_service, d.source]
          .some((v) => (v || "").toLowerCase().includes(q))) return false;
      if (stage && d.stage !== stage) return false;
      if (currency && (d.currency || "USD") !== currency) return false;
      if (owner_id && d.owner_id !== owner_id) return false;
      if (company_id && d.company_id !== company_id) return false;
      if (contact_id && d.contact_id !== contact_id) return false;
      if (close_from && !(d.close_date && d.close_date.slice(0, 10) >= close_from)) return false;
      if (close_to && !(d.close_date && d.close_date.slice(0, 10) <= close_to)) return false;
      return true;
    });
  }, [deals, dealSearch, dealFilters]);

  const dealsByStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const d of filteredDeals) (map[d.stage] || map.new).push(d);
    return map;
  }, [filteredDeals]);

  // Distinct filter options derived from the data
  const industryOptions = useMemo(() => Array.from(new Set(companies.map((c) => c.industry).filter(Boolean))).sort(), [companies]);
  const countryOptions = useMemo(() => Array.from(new Set([...companies.map((c) => c.country), ...contacts.map((c) => c.country)].filter(Boolean))).sort(), [companies, contacts]);
  const leadSourceOptions = useMemo(() => Array.from(new Set([...companies.map((c) => c.lead_source), ...contacts.map((c) => c.lead_source)].filter(Boolean))).sort(), [companies, contacts]);
  const statusOptions = useMemo(() => Array.from(new Set([...companies.map((c) => c.status), ...contacts.map((c) => c.status)].filter(Boolean))).sort(), [companies, contacts]);
  const dealOwnerId = (m) => m.user_id || m.id;
  const memberLabel = (m) => m.name || m.email || (m.user_id ? m.user_id.slice(0, 6) : "Member");
  const activeDealFilters = Object.values(dealFilters).filter(Boolean).length + (dealSearch ? 1 : 0);
  const activeCompanyFilters = Object.values(companyFilters).filter(Boolean).length + (companyQuery ? 1 : 0);
  const activeContactFilters = Object.values(contactFilters).filter(Boolean).length + (contactQuery ? 1 : 0);
  const clearDealFilters = () => { setDealFilters({ stage: "", currency: "", owner_id: "", company_id: "", contact_id: "", close_from: "", close_to: "" }); setDealSearch(""); };
  const clearCompanyFilters = () => { setCompanyFilters({ status: "", owner_id: "", industry: "", country: "", lead_source: "" }); setCompanyQuery(""); };
  const clearContactFilters = () => { setContactFilters({ company_id: "", owner_id: "", country: "", lead_source: "", status: "" }); setContactQuery(""); };

  return (
    <div data-testid="sales-engine">
      <Seo title="Sales" description="Pipeline, companies and contacts." path="/app/sales" />
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow text-brand-teal">Sales Command</p>
          <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Your revenue in motion.</h1>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-testid="deal-open-drawer"
          className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
        >
          <Plus className="h-4 w-4" /> Add deal
        </button>
      </header>

      {/* Compact metrics strip — real workspace signals, per-currency safe */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-testid="sales-metrics">
        <MetricCard testid="metric-pipeline" label="Pipeline">
          {pipelineByCurrency.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active deals yet.</p>
          ) : (
            <ul className="space-y-0.5" data-testid="pipeline-summary">
              {pipelineByCurrency.map((p) => (
                <li key={p.currency} data-testid={`pipeline-summary-${p.currency}`} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="font-semibold">{fmtMoney(p.currency, p.value)}</span>
                  <span className="text-[11px] text-muted-foreground">· {p.count} deal{p.count === 1 ? "" : "s"}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-[10px] text-muted-foreground">Grouped by currency · never combined.</p>
        </MetricCard>
        <MetricCard testid="metric-open" label="Open opportunities" value={salesMetrics.openCount} tint="text-brand-teal" />
        <MetricCard testid="metric-followups" label="Follow-ups due" value={salesMetrics.followUps.length} tint={salesMetrics.followUps.length ? "text-brand-red" : "text-muted-foreground"} />
        <MetricCard testid="metric-closing" label="Expected closes (30d)" value={salesMetrics.closing.length} tint="text-brand-blue" />
        <MetricCard testid="metric-won-lost" label="Won · Lost">
          <p className="text-sm">
            <span className="font-semibold text-brand-green">{salesMetrics.won.length} won</span>
            <span className="mx-1.5 text-muted-foreground">·</span>
            <span className="font-semibold text-brand-red">{salesMetrics.lost.length} lost</span>
          </p>
        </MetricCard>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2" data-testid="sales-tabs">
          {[["pipeline", "Pipeline"], ["companies", "Companies"], ["contacts", "Contacts"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`sales-tab-${k}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${
                tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((s) => !s)}
            data-testid="crm-filters-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              showFilters ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <Filter className="h-3.5 w-3.5" /> Filters
            {tab === "pipeline" && activeDealFilters > 0 ? <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{activeDealFilters}</span> : null}
            {tab === "companies" && activeCompanyFilters > 0 ? <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{activeCompanyFilters}</span> : null}
            {tab === "contacts" && activeContactFilters > 0 ? <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{activeContactFilters}</span> : null}
          </button>
          <CsvControlsV2 kind={tab === "pipeline" ? "deals" : tab} onImported={load} />
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 rounded-2xl border border-border bg-card p-3" data-testid={`crm-filter-panel-${tab}`}>
          {tab === "pipeline" && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative sm:col-span-2 lg:col-span-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input value={dealSearch} onChange={(e) => setDealSearch(e.target.value)} placeholder="Search deal title, notes, product…" className={`${inputCls} pl-8`} data-testid="deal-search-input" />
              </div>
              <select value={dealFilters.stage} onChange={(e) => setDealFilters({ ...dealFilters, stage: e.target.value })} className={inputCls} data-testid="deal-filter-stage">
                <option value="">Any stage</option>
                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <select value={dealFilters.currency} onChange={(e) => setDealFilters({ ...dealFilters, currency: e.target.value })} className={inputCls} data-testid="deal-filter-currency">
                <option value="">Any currency</option>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={dealFilters.company_id} onChange={(e) => setDealFilters({ ...dealFilters, company_id: e.target.value })} className={inputCls} data-testid="deal-filter-company">
                <option value="">Any company</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={dealFilters.contact_id} onChange={(e) => setDealFilters({ ...dealFilters, contact_id: e.target.value })} className={inputCls} data-testid="deal-filter-contact">
                <option value="">Any contact</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.display_name || c.name || c.email}</option>)}
              </select>
              {members.length > 0 && (
                <select value={dealFilters.owner_id} onChange={(e) => setDealFilters({ ...dealFilters, owner_id: e.target.value })} className={inputCls} data-testid="deal-filter-owner">
                  <option value="">Any owner</option>
                  {members.map((m) => <option key={dealOwnerId(m)} value={dealOwnerId(m)}>{memberLabel(m)}</option>)}
                </select>
              )}
              <label className="text-[10px] font-medium text-muted-foreground">
                Closing from
                <input type="date" value={dealFilters.close_from} onChange={(e) => setDealFilters({ ...dealFilters, close_from: e.target.value })} className={`${inputCls} mt-0.5`} data-testid="deal-filter-close-from" />
              </label>
              <label className="text-[10px] font-medium text-muted-foreground">
                Closing to
                <input type="date" value={dealFilters.close_to} onChange={(e) => setDealFilters({ ...dealFilters, close_to: e.target.value })} className={`${inputCls} mt-0.5`} data-testid="deal-filter-close-to" />
              </label>
              {activeDealFilters > 0 && (
                <button onClick={clearDealFilters} className="col-span-full inline-flex items-center gap-1 text-[11px] font-semibold text-brand-teal" data-testid="deal-filter-clear">
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>
          )}
          {tab === "companies" && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <select value={companyFilters.status} onChange={(e) => setCompanyFilters({ ...companyFilters, status: e.target.value })} className={inputCls} data-testid="company-filter-status">
                <option value="">Any status</option>
                {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={companyFilters.industry} onChange={(e) => setCompanyFilters({ ...companyFilters, industry: e.target.value })} className={inputCls} data-testid="company-filter-industry">
                <option value="">Any industry</option>
                {industryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={companyFilters.country} onChange={(e) => setCompanyFilters({ ...companyFilters, country: e.target.value })} className={inputCls} data-testid="company-filter-country">
                <option value="">Any country</option>
                {countryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={companyFilters.lead_source} onChange={(e) => setCompanyFilters({ ...companyFilters, lead_source: e.target.value })} className={inputCls} data-testid="company-filter-lead-source">
                <option value="">Any lead source</option>
                {leadSourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {members.length > 0 && (
                <select value={companyFilters.owner_id} onChange={(e) => setCompanyFilters({ ...companyFilters, owner_id: e.target.value })} className={inputCls} data-testid="company-filter-owner">
                  <option value="">Any owner</option>
                  {members.map((m) => <option key={dealOwnerId(m)} value={dealOwnerId(m)}>{memberLabel(m)}</option>)}
                </select>
              )}
              {activeCompanyFilters > 0 && (
                <button onClick={clearCompanyFilters} className="col-span-full inline-flex items-center gap-1 text-[11px] font-semibold text-brand-teal" data-testid="company-filter-clear">
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>
          )}
          {tab === "contacts" && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <select value={contactFilters.company_id} onChange={(e) => setContactFilters({ ...contactFilters, company_id: e.target.value })} className={inputCls} data-testid="contact-filter-company">
                <option value="">Any company</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={contactFilters.country} onChange={(e) => setContactFilters({ ...contactFilters, country: e.target.value })} className={inputCls} data-testid="contact-filter-country">
                <option value="">Any country</option>
                {countryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={contactFilters.lead_source} onChange={(e) => setContactFilters({ ...contactFilters, lead_source: e.target.value })} className={inputCls} data-testid="contact-filter-lead-source">
                <option value="">Any lead source</option>
                {leadSourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={contactFilters.status} onChange={(e) => setContactFilters({ ...contactFilters, status: e.target.value })} className={inputCls} data-testid="contact-filter-status">
                <option value="">Any status</option>
                {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {members.length > 0 && (
                <select value={contactFilters.owner_id} onChange={(e) => setContactFilters({ ...contactFilters, owner_id: e.target.value })} className={inputCls} data-testid="contact-filter-owner">
                  <option value="">Any owner</option>
                  {members.map((m) => <option key={dealOwnerId(m)} value={dealOwnerId(m)}>{memberLabel(m)}</option>)}
                </select>
              )}
              {activeContactFilters > 0 && (
                <button onClick={clearContactFilters} className="col-span-full inline-flex items-center gap-1 text-[11px] font-semibold text-brand-teal" data-testid="contact-filter-clear">
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : tab === "pipeline" ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="pipeline-board">
            {STAGES.map((s) => (
              <div key={s.key} className="rounded-2xl border border-border bg-card p-4" data-testid={`pipeline-column-${s.key}`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</h3>
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{dealsByStage[s.key]?.length || 0}</span>
                </div>
                <div className="space-y-2">
                  {(dealsByStage[s.key] || []).map((d) => (
                    <DealCard
                      key={d.id}
                      deal={d}
                      companies={companies}
                      contacts={contacts}
                      tasks={tasks}
                      projects={projects}
                      defaultCurrency={defaultCurrency}
                      expanded={expandedDeal === d.id}
                      onExpand={() => setExpandedDeal(expandedDeal === d.id ? null : d.id)}
                      onPatch={(chg) => patchDeal(d.id, chg)}
                      onDelete={() => removeDeal(d.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : tab === "companies" ? (
        <>
          <form onSubmit={addCompany} className="mb-3 flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-3" data-testid="company-form">
            <input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="Company name…" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="company-name-input" />
            <input value={companyForm.industry} onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })} placeholder="Industry" className={inputCls} data-testid="company-industry-input" />
            <button type="submit" data-testid="company-add-btn" className="inline-flex items-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add</button>
          </form>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2" data-testid="company-search-wrap">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              placeholder="Search companies by name, industry, city, country…"
              className="w-full bg-transparent text-sm outline-none"
              data-testid="company-search-input"
            />
            {companyQuery && (
              <button onClick={() => setCompanyQuery("")} className="text-muted-foreground hover:text-foreground" data-testid="company-search-clear"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="company-list">
            {filteredCompanies.length === 0 ? <p className="text-sm text-muted-foreground">No companies match.</p> : filteredCompanies.map((c) => (
              <CompanyCard
                key={c.id}
                company={c}
                contacts={contacts}
                deals={deals}
                tasks={tasks}
                projects={projects}
                expanded={expandedCompany === c.id}
                onExpand={() => setExpandedCompany(expandedCompany === c.id ? null : c.id)}
                onEdit={() => setEditingCompany(c)}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <form onSubmit={addContact} className="mb-3 space-y-2 rounded-2xl border border-border bg-card p-3" data-testid="contact-form">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-full border border-border" data-testid="contact-name-mode">
                <button type="button" onClick={() => setContactNameMode("full")} data-testid="contact-name-mode-full" className={`px-3 py-1 text-[11px] font-semibold ${contactNameMode === "full" ? "brand-gradient-bg text-white" : "text-muted-foreground hover:text-foreground"}`}>Full name</button>
                <button type="button" onClick={() => setContactNameMode("split")} data-testid="contact-name-mode-split" className={`px-3 py-1 text-[11px] font-semibold ${contactNameMode === "split" ? "brand-gradient-bg text-white" : "text-muted-foreground hover:text-foreground"}`}>First + Last</button>
              </div>
              <p className="text-[11px] text-muted-foreground">Either works — Neha Al-Qassimi, María del Carmen López, or split into first/last.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {contactNameMode === "full" ? (
                <input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name…" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="contact-name-input" />
              ) : (
                <>
                  <input value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} placeholder="First name" className={`${inputCls} flex-1 min-w-[140px]`} data-testid="contact-firstname-input" />
                  <input value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} placeholder="Last name" className={`${inputCls} flex-1 min-w-[140px]`} data-testid="contact-lastname-input" />
                </>
              )}
              <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="contact-email-input" />
              <select value={contactForm.company_id} onChange={(e) => setContactForm({ ...contactForm, company_id: e.target.value })} className={inputCls} data-testid="contact-company-select">
                <option value="">No company</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="submit" data-testid="contact-add-btn" className="inline-flex items-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add</button>
            </div>
          </form>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2" data-testid="contact-search-wrap">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
              placeholder="Search contacts by name (full, first, last), email, phone, title…"
              className="w-full bg-transparent text-sm outline-none"
              data-testid="contact-search-input"
            />
            {contactQuery && (
              <button onClick={() => setContactQuery("")} className="text-muted-foreground hover:text-foreground" data-testid="contact-search-clear"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="contact-list">
            {filteredContacts.length === 0 ? <p className="text-sm text-muted-foreground">No contacts match.</p> : filteredContacts.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                companies={companies}
                deals={deals}
                tasks={tasks}
                expanded={expandedContact === c.id}
                onExpand={() => setExpandedContact(expandedContact === c.id ? null : c.id)}
                onEdit={() => setEditingContact(c)}
              />
            ))}
          </div>
        </>
      )}

      {/* Add-deal drawer — kept off the main canvas so it doesn't dominate the page */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="deal-drawer">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 bg-black/40 backdrop-blur-sm"
          />
          <div className="w-full max-w-md overflow-y-auto border-l border-border bg-card p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Add a deal</h2>
              <button onClick={() => setDrawerOpen(false)} data-testid="deal-drawer-close" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={addDeal} className="space-y-3" data-testid="deal-form">
              <label className="block text-xs font-medium text-muted-foreground">
                Deal name
                <input value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} placeholder="e.g. ABC Growth Plan" className={`${inputCls} mt-1`} data-testid="deal-title-input" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs font-medium text-muted-foreground">
                  Stage
                  <select value={dealForm.stage} onChange={(e) => setDealForm({ ...dealForm, stage: e.target.value })} className={`${inputCls} mt-1`} data-testid="deal-stage-select">
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
                <label className="block text-xs font-medium text-muted-foreground">
                  Currency
                  <select value={dealForm.currency || defaultCurrency} onChange={(e) => setDealForm({ ...dealForm, currency: e.target.value })} className={`${inputCls} mt-1`} data-testid="deal-currency-select">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <label className="block text-xs font-medium text-muted-foreground">
                Value
                <input type="number" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} placeholder="Amount" className={`${inputCls} mt-1`} data-testid="deal-value-input" />
              </label>
              <p className="text-[11px] text-muted-foreground">You can add primary contact, next follow-up and notes after creating the deal.</p>
              <button type="submit" data-testid="deal-add-btn" className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white">
                <Plus className="h-4 w-4" /> Add deal
              </button>
            </form>
          </div>
        </div>
      )}
      <CompanyEditDrawer
        company={editingCompany}
        onClose={() => setEditingCompany(null)}
        onSave={patchCompany}
        onDelete={removeCompany}
      />
      <ContactEditDrawer
        contact={editingContact}
        companies={companies}
        onClose={() => setEditingContact(null)}
        onSave={patchContact}
        onDelete={removeContact}
      />
      <LifecycleDraftModal
        trigger="deal_won"
        entityId={lifecycleDeal?.id}
        entityLabel={lifecycleDeal?.title}
        open={!!lifecycleDeal}
        onClose={() => setLifecycleDeal(null)}
        onApproved={() => setLifecycleDeal(null)}
      />
    </div>
  );
}

function MetricCard({ label, value, tint = "text-foreground", children, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {typeof value === "number" ? (
        <p className={`mt-1 font-display text-2xl font-semibold ${tint}`}>{value}</p>
      ) : (
        <div className="mt-1">{children}</div>
      )}
    </div>
  );
}

function DealCard({ deal, companies, contacts, tasks, projects = [], defaultCurrency, expanded, onExpand, onPatch, onDelete }) {
  const co = companies.find((c) => c.id === deal.company_id);
  const ct = contacts.find((c) => c.id === deal.contact_id);
  const related = tasks.filter((t) => t.deal_id === deal.id);
  const relatedProjects = projects.filter((p) => p.deal_id === deal.id);
  const today = new Date().toISOString().slice(0, 10);
  const followUpDue = deal.next_follow_up && deal.next_follow_up.slice(0, 10) <= today;

  return (
    <div className="rounded-xl border border-border bg-background p-3" data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={onExpand} data-testid={`deal-expand-${deal.id}`} className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm font-medium">{deal.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {fmtMoney(deal.currency || defaultCurrency, deal.value)}
            {co ? ` · ${co.name}` : ""}
          </p>
        </button>
        <button onClick={onDelete} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <select value={deal.stage} onChange={(e) => onPatch({ stage: e.target.value })} data-testid={`deal-stage-${deal.id}`} className="flex-1 rounded-lg border border-border bg-card px-2 py-1 text-xs">
          {STAGES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <button onClick={onExpand} className="text-muted-foreground hover:text-foreground" aria-label="Details">
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {followUpDue && (
        <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-brand-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand-red ring-1 ring-brand-red/30" data-testid={`deal-followup-due-${deal.id}`}>
          <Bell className="h-2.5 w-2.5" /> Follow-up due
        </p>
      )}

      {expanded && (
        <div className="mt-3 space-y-2 rounded-lg bg-muted/40 p-3" data-testid={`deal-detail-${deal.id}`}>
          <label className="block text-[11px] font-medium text-muted-foreground">
            Primary contact
            <select
              defaultValue={deal.contact_id || ""}
              onChange={(e) => onPatch({ contact_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`deal-contact-${deal.id}`}
            >
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-[11px] font-medium text-muted-foreground">
            Company
            <select
              defaultValue={deal.company_id || ""}
              onChange={(e) => onPatch({ company_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`deal-company-${deal.id}`}
            >
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[11px] font-medium text-muted-foreground">
              Value
              <input
                type="number"
                defaultValue={deal.value ?? 0}
                onBlur={(e) => Number(e.target.value) !== Number(deal.value) && onPatch({ value: Number(e.target.value) || 0 })}
                className={`${inputCls} mt-1`}
                data-testid={`deal-value-${deal.id}`}
              />
            </label>
            <label className="block text-[11px] font-medium text-muted-foreground">
              Currency
              <select
                defaultValue={deal.currency || defaultCurrency}
                onChange={(e) => onPatch({ currency: e.target.value })}
                className={`${inputCls} mt-1`}
                data-testid={`deal-currency-${deal.id}`}
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block text-[11px] font-medium text-muted-foreground">
              Expected close
              <input
                type="date"
                defaultValue={deal.close_date ? deal.close_date.slice(0, 10) : ""}
                onBlur={(e) => e.target.value !== (deal.close_date || "") && onPatch({ close_date: e.target.value || null })}
                className={`${inputCls} mt-1`}
                data-testid={`deal-closedate-${deal.id}`}
              />
            </label>
            <label className="block text-[11px] font-medium text-muted-foreground">
              Next follow-up
              <input
                type="date"
                defaultValue={deal.next_follow_up ? deal.next_follow_up.slice(0, 10) : ""}
                onBlur={(e) => e.target.value !== (deal.next_follow_up || "") && onPatch({ next_follow_up: e.target.value || null })}
                className={`${inputCls} mt-1`}
                data-testid={`deal-followup-${deal.id}`}
              />
            </label>
          </div>
          <label className="block text-[11px] font-medium text-muted-foreground">
            Notes
            <textarea
              rows={2}
              defaultValue={deal.notes || ""}
              onBlur={(e) => e.target.value !== (deal.notes || "") && onPatch({ notes: e.target.value })}
              className={`${inputCls} mt-1 resize-none`}
              data-testid={`deal-notes-${deal.id}`}
            />
          </label>
          {ct && <p className="text-[11px] text-muted-foreground">Contact: <span className="font-medium text-foreground">{ct.name}</span>{ct.email ? ` · ${ct.email}` : ""}</p>}
          {related.length > 0 && (
            <div className="mt-2 border-t border-border pt-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Related work</p>
              <ul className="space-y-1 text-xs">
                {related.slice(0, 4).map((t) => (
                  <li key={t.id} className={t.status === "done" ? "text-muted-foreground line-through" : ""}>
                    {t.title}{t.due_at ? ` — due ${t.due_at.slice(0, 10)}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {relatedProjects.length > 0 && (
            <div className="mt-2 border-t border-border pt-2" data-testid={`deal-projects-${deal.id}`}>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Related projects</p>
              <ul className="space-y-1 text-xs">
                {relatedProjects.slice(0, 4).map((p) => (
                  <li key={p.id}>{p.name} — {p.status}{p.progress ? ` · ${p.progress}%` : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompanyCard({ company, contacts, deals, tasks, projects = [], expanded, onExpand, onEdit }) {
  const coContacts = contacts.filter((c) => c.company_id === company.id);
  const coDeals = deals.filter((d) => d.company_id === company.id);
  const coTasks = tasks.filter((t) => t.company_id === company.id);
  const coProjects = projects.filter((p) => p.company_id === company.id);
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`company-card-${company.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={onExpand} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`company-expand-${company.id}`}>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><Building2 className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{company.name}</p>
            <p className="text-xs text-muted-foreground">
              {[company.industry, [company.city, company.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
              {` · ${coContacts.length} contact${coContacts.length === 1 ? "" : "s"} · ${coDeals.length} deal${coDeals.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </button>
        {onEdit && (
          <button onClick={onEdit} data-testid={`company-edit-${company.id}`} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={onExpand} className="rounded-md p-1 text-muted-foreground hover:text-foreground" aria-label="Toggle">
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-border pt-3 text-xs text-muted-foreground" data-testid={`company-detail-${company.id}`}>
          <CompanyFactsGrid company={company} />
          {coContacts.length > 0 && <RelatedList label="Contacts" icon={ContactIcon} items={coContacts.map((c) => c.display_name || c.name)} />}
          {coDeals.length > 0 && <RelatedList label="Deals" icon={TrendingUp} items={coDeals.map((d) => `${d.title} — ${d.stage}`)} />}
          {coProjects.length > 0 && <RelatedList label="Projects" items={coProjects.map((p) => `${p.name} — ${p.status}`)} />}
          {coTasks.length > 0 && <RelatedList label="Related work" items={coTasks.slice(0, 4).map((t) => t.title)} />}
          {company.notes && <p className="whitespace-pre-wrap">{company.notes}</p>}
        </div>
      )}
    </div>
  );
}

function CompanyFactsGrid({ company }) {
  const rows = [
    ["Type", company.company_type],
    ["Website", company.website],
    ["Email", company.email],
    ["Phone", company.phone],
    ["LinkedIn", company.linkedin],
    ["Address", [company.address, company.city, company.state, company.postal_code, company.country].filter(Boolean).join(", ")],
    ["Lead source", company.lead_source],
    ["Status", company.status],
    ["Last interaction", company.last_interaction ? company.last_interaction.slice(0, 10) : ""],
    ["Next follow-up", company.next_follow_up ? company.next_follow_up.slice(0, 10) : ""],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid={`company-facts-${company.id}`}>
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{k}</dt><dd className="truncate text-foreground">{v}</dd></div>
      ))}
    </dl>
  );
}

function ContactCard({ contact, companies, deals, tasks, expanded, onExpand, onEdit }) {
  const co = companies.find((c) => c.id === contact.company_id);
  const ctDeals = deals.filter((d) => d.contact_id === contact.id);
  const ctTasks = tasks.filter((t) => t.contact_id === contact.id);
  const display = contact.display_name || contact.name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`contact-card-${contact.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={onExpand} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`contact-expand-${contact.id}`}>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><Users className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{display || "Unnamed contact"}</p>
            <p className="truncate text-xs text-muted-foreground">
              {[contact.job_title || contact.role, co?.name, contact.email].filter(Boolean).join(" · ") || "—"}
            </p>
          </div>
        </button>
        {onEdit && (
          <button onClick={onEdit} data-testid={`contact-edit-${contact.id}`} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <button onClick={onExpand} className="rounded-md p-1 text-muted-foreground hover:text-foreground" aria-label="Toggle">
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-border pt-3 text-xs text-muted-foreground" data-testid={`contact-detail-${contact.id}`}>
          <ContactFactsGrid contact={contact} company={co} />
          {ctDeals.length > 0 && <RelatedList label="Deals" icon={TrendingUp} items={ctDeals.map((d) => `${d.title} — ${d.stage}`)} />}
          {ctTasks.length > 0 && <RelatedList label="Related work" items={ctTasks.slice(0, 4).map((t) => t.title)} />}
          {contact.notes && <p className="whitespace-pre-wrap">{contact.notes}</p>}
        </div>
      )}
    </div>
  );
}

function ContactFactsGrid({ contact, company }) {
  const rows = [
    ["Job title", contact.job_title || contact.role],
    ["Company", company?.name],
    ["Email", contact.email],
    ["Secondary email", contact.secondary_email],
    ["Phone", contact.phone],
    ["Mobile / WhatsApp", contact.mobile],
    ["LinkedIn", contact.linkedin],
    ["Website", contact.website],
    ["Location", [contact.city, contact.state, contact.country].filter(Boolean).join(", ")],
    ["Postal code", contact.postal_code],
    ["Lead source", contact.lead_source],
    ["Status", contact.status],
    ["Last interaction", contact.last_interaction ? contact.last_interaction.slice(0, 10) : ""],
    ["Next follow-up", contact.next_follow_up ? contact.next_follow_up.slice(0, 10) : ""],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid={`contact-facts-${contact.id}`}>
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{k}</dt><dd className="truncate text-foreground">{v}</dd></div>
      ))}
    </dl>
  );
}

function RelatedList({ label, icon: Icon, items }) {
  return (
    <div>
      <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {Icon ? <Icon className="h-3 w-3" /> : null}{label}
      </p>
      <ul className="space-y-1">
        {items.map((it, i) => <li key={i} className="text-foreground/80">{it}</li>)}
      </ul>
    </div>
  );
}

const COMPANY_FIELDS = [
  { key: "name", label: "Company Name", required: true },
  { key: "company_type", label: "Company Type", type: "select", options: ["", "Prospect", "Customer", "Partner", "Vendor", "Investor", "Other"] },
  { key: "industry", label: "Industry" },
  { key: "website", label: "Website", placeholder: "https://…" },
  { key: "email", label: "Company Email", type: "email" },
  { key: "phone", label: "Main Phone" },
  { key: "linkedin", label: "LinkedIn URL", placeholder: "https://linkedin.com/company/…" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State / Province" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country", label: "Country" },
  { key: "lead_source", label: "Lead Source", placeholder: "Referral, LinkedIn, Event…" },
  { key: "status", label: "Status", type: "select", options: ["", "Active", "Inactive", "Nurturing", "Churned"] },
  { key: "last_interaction", label: "Last Interaction", type: "date" },
  { key: "next_follow_up", label: "Next Follow-up", type: "date" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

function CompanyEditDrawer({ company, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!company) return;
    const seeded = {};
    COMPANY_FIELDS.forEach((f) => {
      const v = company[f.key];
      seeded[f.key] = f.type === "date" && v ? String(v).slice(0, 10) : (v ?? "");
    });
    setForm(seeded);
  }, [company]);
  if (!company) return null;
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Company Name is required."); return; }
    setSaving(true);
    const payload = {};
    COMPANY_FIELDS.forEach((f) => {
      const original = company[f.key];
      const originalStr = f.type === "date" && original ? String(original).slice(0, 10) : (original ?? "");
      if ((form[f.key] ?? "") !== originalStr) payload[f.key] = form[f.key] ?? "";
    });
    if (Object.keys(payload).length === 0) { setSaving(false); onClose(); return; }
    const res = await onSave(company.id, payload);
    setSaving(false);
    if (res) onClose();
  };
  const confirmDelete = () => {
    if (window.confirm(`Delete "${company.name}"? This can't be undone.`)) onDelete(company.id);
  };
  return (
    <EntityDrawer title={company.name} subtitle="Company details" onClose={onClose} testid="company-edit-drawer">
      <FormGrid fields={COMPANY_FIELDS} form={form} setForm={setForm} testidPrefix="company-edit" />
      <DrawerFooter onSave={save} onDelete={confirmDelete} onClose={onClose} saving={saving} testidPrefix="company-edit" />
    </EntityDrawer>
  );
}

const CONTACT_FIELDS_BASE = [
  { key: "job_title", label: "Job Title" },
  { key: "email", label: "Email", type: "email" },
  { key: "secondary_email", label: "Secondary Email", type: "email" },
  { key: "phone", label: "Phone" },
  { key: "mobile", label: "Mobile / WhatsApp" },
  { key: "linkedin", label: "LinkedIn URL", placeholder: "https://linkedin.com/in/…" },
  { key: "website", label: "Website" },
  { key: "city", label: "City" },
  { key: "state", label: "State / Province" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country", label: "Country" },
  { key: "lead_source", label: "Lead Source" },
  { key: "status", label: "Status", type: "select", options: ["", "New", "Working", "Qualified", "Customer", "Dormant"] },
  { key: "last_interaction", label: "Last Interaction", type: "date" },
  { key: "next_follow_up", label: "Next Follow-up", type: "date" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

function ContactEditDrawer({ contact, companies, onClose, onSave, onDelete }) {
  const [nameMode, setNameMode] = useState("full");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!contact) return;
    const seeded = {
      display_name: contact.display_name || contact.name || "",
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      company_id: contact.company_id || "",
    };
    CONTACT_FIELDS_BASE.forEach((f) => {
      const v = contact[f.key];
      seeded[f.key] = f.type === "date" && v ? String(v).slice(0, 10) : (v ?? "");
    });
    // legacy 'role' field falls back to job_title
    if (!seeded.job_title && contact.role) seeded.job_title = contact.role;
    setForm(seeded);
    setNameMode(contact.first_name || contact.last_name ? "split" : "full");
  }, [contact]);
  if (!contact) return null;

  const save = async () => {
    const anyName = (nameMode === "full" ? form.display_name?.trim() : (form.first_name?.trim() || form.last_name?.trim())) || form.email?.trim();
    if (!anyName) { toast.error("Provide a name or email."); return; }
    setSaving(true);
    const payload = {};
    if (nameMode === "full") {
      payload.display_name = form.display_name || "";
      payload.first_name = "";
      payload.last_name = "";
    } else {
      payload.first_name = form.first_name || "";
      payload.last_name = form.last_name || "";
      payload.display_name = "";
    }
    if ((form.company_id ?? "") !== (contact.company_id ?? "")) payload.company_id = form.company_id || null;
    CONTACT_FIELDS_BASE.forEach((f) => {
      const original = contact[f.key];
      const originalStr = f.type === "date" && original ? String(original).slice(0, 10) : (original ?? "");
      if ((form[f.key] ?? "") !== originalStr) payload[f.key] = form[f.key] ?? "";
    });
    const res = await onSave(contact.id, payload);
    setSaving(false);
    if (res) onClose();
  };
  const confirmDelete = () => {
    if (window.confirm(`Delete "${contact.display_name || contact.name || "this contact"}"? This can't be undone.`)) onDelete(contact.id);
  };
  return (
    <EntityDrawer title={contact.display_name || contact.name || "Contact"} subtitle="Contact details" onClose={onClose} testid="contact-edit-drawer">
      <div className="mb-3 flex items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-full border border-border" data-testid="contact-edit-name-mode">
          <button type="button" onClick={() => setNameMode("full")} data-testid="contact-edit-name-mode-full" className={`px-3 py-1 text-[11px] font-semibold ${nameMode === "full" ? "brand-gradient-bg text-white" : "text-muted-foreground"}`}>Full name</button>
          <button type="button" onClick={() => setNameMode("split")} data-testid="contact-edit-name-mode-split" className={`px-3 py-1 text-[11px] font-semibold ${nameMode === "split" ? "brand-gradient-bg text-white" : "text-muted-foreground"}`}>First + Last</button>
        </div>
        <p className="text-[11px] text-muted-foreground">Names are preserved exactly as entered.</p>
      </div>
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        {nameMode === "full" ? (
          <FieldInput field={{ key: "display_name", label: "Full Name", full: true }} form={form} setForm={setForm} testidPrefix="contact-edit" />
        ) : (
          <>
            <FieldInput field={{ key: "first_name", label: "First Name" }} form={form} setForm={setForm} testidPrefix="contact-edit" />
            <FieldInput field={{ key: "last_name", label: "Last Name" }} form={form} setForm={setForm} testidPrefix="contact-edit" />
          </>
        )}
        <label className="flex flex-col text-xs font-medium sm:col-span-2" data-testid="contact-edit-company-wrap">
          <span className="mb-1 text-muted-foreground">Company</span>
          <select value={form.company_id || ""} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className={inputCls} data-testid="contact-edit-field-company_id">
            <option value="">No company</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>
      <FormGrid fields={CONTACT_FIELDS_BASE} form={form} setForm={setForm} testidPrefix="contact-edit" />
      <DrawerFooter onSave={save} onDelete={confirmDelete} onClose={onClose} saving={saving} testidPrefix="contact-edit" />
    </EntityDrawer>
  );
}

function EntityDrawer({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid} role="dialog" aria-modal="true">
      <button className="flex-1 bg-black/40" onClick={onClose} aria-label="Close" data-testid={`${testid}-backdrop`} />
      <div className="flex h-full w-full max-w-lg flex-col overflow-y-auto border-l border-border bg-background shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 px-5 py-4 backdrop-blur">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{subtitle}</p>
            <h3 className="mt-0.5 font-display text-base font-semibold tracking-tight truncate">{title}</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Close" data-testid={`${testid}-close`}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-3 px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function FormGrid({ fields, form, setForm, testidPrefix }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => (
        <FieldInput key={f.key} field={f} form={form} setForm={setForm} testidPrefix={testidPrefix} />
      ))}
    </div>
  );
}

function FieldInput({ field, form, setForm, testidPrefix }) {
  const value = form[field.key] ?? "";
  const onChange = (v) => setForm({ ...form, [field.key]: v });
  const commonProps = {
    value,
    onChange: (e) => onChange(e.target.value),
    className: inputCls,
    placeholder: field.placeholder || "",
    "data-testid": `${testidPrefix}-field-${field.key}`,
  };
  const wrap = (input) => (
    <label className={`flex flex-col text-xs font-medium ${field.full ? "sm:col-span-2" : ""}`}>
      <span className="mb-1 text-muted-foreground">{field.label}{field.required ? " *" : ""}</span>
      {input}
    </label>
  );
  if (field.type === "textarea") return wrap(<textarea rows={4} {...commonProps} className={`${inputCls} resize-none`} />);
  if (field.type === "select") return wrap(
    <select {...commonProps}>{field.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}</select>
  );
  return wrap(<input type={field.type || "text"} {...commonProps} />);
}

function DrawerFooter({ onSave, onDelete, onClose, saving, testidPrefix }) {
  return (
    <div className="sticky bottom-0 -mx-5 mt-4 flex items-center justify-between border-t border-border bg-background/95 px-5 py-3 backdrop-blur">
      <button onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brand-red hover:bg-brand-red/10" data-testid={`${testidPrefix}-delete`}>
        <Trash2 className="h-3 w-3" /> Delete
      </button>
      <div className="flex items-center gap-2">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-cancel`}>Cancel</button>
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60" data-testid={`${testidPrefix}-save`}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

